import React from 'react';
import PropTypes from 'prop-types';
import { 
  Card, 
  CardHeader,
  CardContent, 
  Typography, 
  Box, 
  Chip,
  useTheme,
  alpha,
  Avatar
} from '@mui/material';
import { DESIGN_SYSTEM } from '../../theme/designSystem';

/**
 * Componente de Card Premium Unificado
 * Estética consistente y moderna para todo el dashboard
 */
export default function PremiumCard({ 
  title, 
  subtitle, 
  children, 
  action, 
  icon, 
  color = 'primary',
  variant = 'elevated',
  exclusive = false,
  ...props 
}) {
  const theme = useTheme();
  
  const getColorPalette = (colorName) => {
    const colors = {
      primary: theme.palette.primary,
      secondary: theme.palette.secondary,
      success: theme.palette.success,
      warning: theme.palette.warning,
      error: theme.palette.error,
      info: theme.palette.info,
    };
    return colors[colorName] || colors.primary;
  };

  const colorPalette = getColorPalette(color);

  const cardStyles = {
    elevated: {
      ...DESIGN_SYSTEM.card,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
      borderRadius: 3,
      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.04)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.08)}`,
        borderColor: alpha(colorPalette.main, 0.16),
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        background: `linear-gradient(90deg, ${colorPalette.main} 0%, ${colorPalette.light} 100%)`,
        borderRadius: '12px 12px 0 0',
      }
    },
    glass: {
      ...DESIGN_SYSTEM.card,
      background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
      backdropFilter: 'blur(20px)',
      border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
      borderRadius: 4,
      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
    }
  };

  return (
    <Card 
      sx={cardStyles[variant]}
      {...props}
    >
      <CardHeader
        avatar={icon && (
          <Avatar 
            sx={{ 
              bgcolor: alpha(colorPalette.main, 0.1),
              color: colorPalette.main,
              width: 48,
              height: 48,
            }}
          >
            {icon}
          </Avatar>
        )}
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <Typography 
              variant="h6" 
              fontWeight={600}
              color="text.primary"
              sx={{ 
                background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${colorPalette.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {title}
            </Typography>
            {exclusive && (
              <Chip 
                label="EXCLUSIVO" 
                size="small" 
                color={color}
                variant="outlined"
                sx={{ 
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  borderWidth: 2,
                }}
              />
            )}
          </Box>
        }
        subheader={subtitle && (
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            {subtitle}
          </Typography>
        )}
        action={action}
        sx={{ 
          pb: 1,
          '& .MuiCardHeader-content': {
            overflow: 'visible'
          }
        }}
      />
      <CardContent sx={{ pt: 0 }}>
        {children}
      </CardContent>
    </Card>
  );
}

PremiumCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  action: PropTypes.node,
  icon: PropTypes.node,
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'error', 'info']),
  variant: PropTypes.oneOf(['elevated', 'glass']),
  exclusive: PropTypes.bool,
};
